/**
 * IMPORTS
 */
import accountSelectors from 'src/aggregates/accounts/selectors';
import conversationSelectors from 'src/aggregates/conversations/selectors';
import customerSelectors from 'src/aggregates/customers/selectors';
import {contactTypesChannelToId} from 'src/aggregates/customers/utils';
import {contactTypesIdToChannel} from 'src/aggregates/customers/utils';
import {isActive} from 'src/aggregates/customers/utils';


/**
 * TYPES
 */
import {IReducedData} from 'src/utils/getreduceddata.d';


/**
 * CODE
 */

/**
 * I get situational data from the given customer.
 *
 * :param customer: customer id
 *
 * :returns: customer situational data
 */
function getReducedData (customer: string | number): IReducedData
{
    // get customer contacts and display name
    const contacts = customerSelectors.fetch(customer)?.contacts ?? [];

    // get the first contact with an active channel
    const contact = contacts.find(contact =>
        isActive(contact.type_id, accountSelectors.getChannels()),
    ) ?? null;

    // no contact with an active channel: fallback to default behavior
    if (contact === null)
    {
        return {
            account: null,
            channel: null,
            contactId: null,
            contactName: null,
            conversationId: null,
            typeId: null,
        };
    }

    // found contact with active channel: get customer conversations
    const conversations = conversationSelectors.getConversationsByCustomer(
        customer,
    );

    // has conversations: get props from conversation
    if (conversations.length > 0)
    {
        // get props from conversation with last interaction
        const {account, id, channel, remote: uid} = conversations.reduce(
            (previous, current) =>
                previous.lastInteraction > current.lastInteraction
                    ? previous
                    : current,
        );

        // get conversation contact
        const contact = customerSelectors.getContactInCustomer(
            contactTypesChannelToId.get(channel),
            customer,
            uid,
        );

        // contact exists: returns data from conversation with last interaction
        if (contact !== null)
        {
            return {
                account,
                channel,
                contactId: contact.id,
                contactName: contact.name ?? null,
                conversationId: channel === 'email' ? null : id,
                typeId: contact.type_id,
            };
        }
    }

    // get type id, channel and account id
    const typeId = contact.type_id,
          channel = contactTypesIdToChannel.get(typeId).toLowerCase(),
          account = accountSelectors.getByChannel(channel)?.id ?? null;

    // return customer situational data
    return {
        account,
        channel,
        contactId: contact.id,
        contactName: contact.name ?? null,
        conversationId: null,
        typeId,
    };
}


/**
 * EXPORTS
 */
export {
    getReducedData,
};
